'use client';
import { usePathname } from 'next/navigation';
import { createContext, useContext, useMemo } from 'react';
import { useSessionValues } from './hooks/use-session-values';
import type { CurrentUser, SessionValues } from './types';
import { useGetCurrentUser } from './use-current-user';
import { isAnonymousRoute, isLoginRoute } from './utils';

export type User = CurrentUser;

type Session = {
  user: User;
  session: SessionValues;
};

export type SessionContextValue<R extends boolean = false> = R extends true
  ? { data: Session; status: 'authenticated'; isAuthenticated: true }
  :
      | { data: Session; status: 'authenticated'; isAuthenticated: true }
      | {
          data: null;
          status: 'unauthenticated' | 'loading';
          isAuthenticated: false;
        };

export const SessionContext = createContext?.<SessionContextValue | undefined>(
  undefined
);

export interface UseSessionOptions<R extends boolean> {
  required: R;
}

export function useSession<R extends boolean = true>(
  options?: UseSessionOptions<R>
): SessionContextValue<R> {
  if (!SessionContext) {
    throw new Error('React Context is unavailable in Server Components');
  }

  const value = useContext(SessionContext);

  if (!value) {
    throw new Error('`useSession` must be wrapped in a <SessionProvider />');
  }

  const { required } = options || { required: true };

  if (required) {
    if (!value.data) {
      throw new Error(
        'Session is required but not found, you may need to reload the page'
      );
    }
  }

  return value as SessionContextValue<R>;
}

export interface SessionProviderProps {
  children: React.ReactNode;
}

export function SessionProvider(props: SessionProviderProps) {
  if (!SessionContext) {
    throw new Error('React Context is unavailable in Server Components');
  }

  // useRefreshValidation();
  const sessionValues = useSessionValues();
  const token = sessionValues.token;
  const isExpired = sessionValues.isExpired;
  const pathname = usePathname();
  const isSpecialRoute = isAnonymousRoute(pathname) || isLoginRoute(pathname);

  const { data: user, isPending: isLoading } = useGetCurrentUser({
    enabled: !isSpecialRoute,
  });

  const value = useMemo((): SessionContextValue => {
    const data = (user ? { user } : null) ?? null;

    if (isSpecialRoute) {
      return {
        data: null,
        status: 'unauthenticated',
        isAuthenticated: false,
      };
    }

    if (!token || isExpired) {
      return {
        data: null,
        status: 'unauthenticated',
        isAuthenticated: false,
      };
    }

    if (isLoading) {
      return {
        data: null,
        status: 'loading',
        isAuthenticated: false,
      };
    }

    if (data?.user) {
      return {
        data: {
          user: data.user,
          session: sessionValues,
        },
        status: 'authenticated',
        isAuthenticated: true,
      };
    }
    return {
      data: null,
      status: 'unauthenticated',
      isAuthenticated: false,
    };
  }, [user, isLoading, isSpecialRoute, sessionValues, token, isExpired]);

  return (
    <SessionContext.Provider value={value}>
      {props.children}
    </SessionContext.Provider>
  );
}
